import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { Check, TriangleAlert, LoaderCircle } from 'lucide-react';

// Recovery links can arrive in three shapes, and can also arrive already spent:
//   ?token_hash=...&type=recovery   — what our templates emit (scanner-safe)
//   ?code=...                       — PKCE, if flowType ever changes
//   #access_token=...&type=recovery — implicit; supabase-js consumes it itself
//   #error=...&error_code=...       — link expired, or already used
// The page must settle which one it is BEFORE showing a password form —
// otherwise the user types a new password and only then learns the link is
// dead, which is the "Auth session missing!" report from the field.
export default function ResetPasswordPage() {
  const {
    session,
    loading: authLoading,
    updatePassword,
    resetPassword,
    verifyRecoveryToken,
    exchangeRecoveryCode,
  } = useAuth();
  const navigate = useNavigate();

  const [phase, setPhase] = useState('verifying'); // verifying | ready | invalid | done
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Resend escape hatch, shown when the link is dead.
  const [resendEmail, setResendEmail] = useState('');
  const [resent, setResent] = useState(false);

  const verifiedRef = useRef(false);

  useEffect(() => {
    // A single-use token must only be redeemed once — StrictMode double-invokes
    // effects in development, and the second call would consume nothing and fail.
    if (verifiedRef.current) return;
    verifiedRef.current = true;

    let alive = true;
    const search = new URLSearchParams(window.location.search);
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const pick = (key) => search.get(key) || hash.get(key);

    const urlError = pick('error_description') || pick('error') || pick('error_code');
    const tokenHash = search.get('token_hash');
    const code = search.get('code');

    const dead = (msg) => {
      if (!alive) return;
      setError(msg);
      setPhase('invalid');
    };

    (async () => {
      if (urlError) {
        dead(friendlyLinkError(urlError));
        return;
      }

      if (tokenHash) {
        const { error: err } = await verifyRecoveryToken(tokenHash);
        if (!alive) return;
        if (err) return dead(friendlyLinkError(err.message));
        setPhase('ready');
        return;
      }

      if (code) {
        const { error: err } = await exchangeRecoveryCode(code);
        if (!alive) return;
        if (err) return dead(friendlyLinkError(err.message));
        setPhase('ready');
        return;
      }
      // No recognisable parameters: either the implicit hash that supabase-js
      // already swallowed, or an already-signed-in user changing their password.
      // Both surface as a session appearing — handled by the effect below.
    })();

    return () => { alive = false; };
  }, [verifyRecoveryToken, exchangeRecoveryCode]);

  // Implicit flow lands the session asynchronously, so give it a moment to
  // appear before declaring the link dead.
  useEffect(() => {
    if (phase !== 'verifying') return undefined;
    if (session) { setPhase('ready'); return undefined; }
    if (authLoading) return undefined;

    const timer = setTimeout(() => {
      setPhase((p) => (p === 'verifying' ? 'invalid' : p));
      setError((e) => e || 'This password reset link is no longer valid. It may have expired or already been used.');
    }, 3000);
    return () => clearTimeout(timer);
  }, [phase, session, authLoading]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    const { error: err } = await updatePassword(password);
    if (err) {
      setError(err.message);
      setLoading(false);
      return;
    }

    setPhase('done');
    setLoading(false);
    setTimeout(() => navigate('/login', { replace: true }), 2000);
  };

  const handleResend = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const { error: err } = await resetPassword(resendEmail);
    setLoading(false);
    if (err) {
      setError(err.message);
      return;
    }
    setResent(true);
  };

  const inputClass = 'w-full px-3.5 py-[11px] bg-tertiary border border-strong rounded-[10px] text-primary-c text-[13px] font-[inherit] transition-all focus:outline-none focus:border-[#0DA7CA] focus:shadow-[0_0_0_3px_rgba(13, 167, 202,0.15)]';
  const buttonClass = 'w-full py-[11px] rounded-[10px] text-[13px] font-semibold text-white gradient-accent shadow-glow-lg hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0';

  if (phase === 'done') {
    return (
      <Centered>
        <div className="w-[48px] h-[48px] bg-success-soft text-success rounded-[14px] flex items-center justify-center mx-auto mb-4">
          <Check size={24} strokeWidth={2.5} />
        </div>
        <h1 className="text-[22px] font-bold text-primary-c mb-2">Password updated</h1>
        <p className="text-[13px] text-muted-c">Redirecting to sign in...</p>
      </Centered>
    );
  }

  if (phase === 'verifying') {
    return (
      <Centered>
        <div className="w-[48px] h-[48px] bg-accent-glow text-accent-light rounded-[14px] flex items-center justify-center mx-auto mb-4">
          <LoaderCircle size={24} strokeWidth={2.5} className="animate-spin" />
        </div>
        <h1 className="text-[22px] font-bold text-primary-c mb-2">Checking your link</h1>
        <p className="text-[13px] text-muted-c">One moment...</p>
      </Centered>
    );
  }

  if (phase === 'invalid') {
    return (
      <Centered>
        <div className="w-[48px] h-[48px] bg-danger-soft text-danger rounded-[14px] flex items-center justify-center mx-auto mb-4">
          <TriangleAlert size={24} strokeWidth={2.5} />
        </div>
        <h1 className="text-[22px] font-bold text-primary-c mb-2">This link can't be used</h1>
        <p className="text-[13px] text-muted-c mb-6">{error}</p>

        {resent ? (
          <p className="text-[13px] text-muted-c mb-6">
            If an account exists for <strong className="text-primary-c">{resendEmail}</strong>,
            a new reset link is on its way.
          </p>
        ) : (
          <div className="bg-elevated border border-theme rounded-xl p-6 shadow-lg text-left mb-5">
            <form onSubmit={handleResend} className="space-y-4">
              <div>
                <label className="block text-[12px] font-semibold text-secondary-c mb-2">
                  Send a new link
                </label>
                <input
                  type="email"
                  className={inputClass}
                  placeholder="you@example.com"
                  value={resendEmail}
                  onChange={(ev) => setResendEmail(ev.target.value)}
                  required
                />
              </div>
              <button type="submit" disabled={loading} className={buttonClass}>
                {loading ? 'Sending...' : 'Email me a new link'}
              </button>
            </form>
          </div>
        )}

        <Link to="/login" className="text-[13px] font-semibold text-accent-light hover:text-accent transition-colors">
          Back to sign in
        </Link>
      </Centered>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-primary p-4">
      <div className="w-full max-w-[420px]">
        <div className="flex flex-col items-center mb-8">
          <img src="/logo.svg" alt="MediLink" className="h-[64px] w-auto mb-4" />
          <h1 className="text-[22px] font-bold text-primary-c">Set new password</h1>
          <p className="text-[13px] text-muted-c mt-1">Choose a strong password</p>
        </div>

        <div className="bg-elevated border border-theme rounded-xl p-6 md:p-7 shadow-lg">
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="px-4 py-3 rounded-[10px] bg-danger-soft text-danger text-[13px] font-medium">
                {error}
              </div>
            )}

            <div>
              <label className="block text-[12px] font-semibold text-secondary-c mb-2">New Password</label>
              <input
                type="password"
                className={inputClass}
                placeholder="Minimum 8 characters"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                minLength={8}
                autoFocus
              />
            </div>

            <div>
              <label className="block text-[12px] font-semibold text-secondary-c mb-2">Confirm Password</label>
              <input
                type="password"
                className={inputClass}
                placeholder="Re-enter password"
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                required
              />
            </div>

            <button type="submit" disabled={loading} className={buttonClass}>
              {loading ? 'Updating...' : 'Update Password'}
            </button>
          </form>
        </div>

        <p className="text-center text-[13px] text-muted-c mt-5">
          <Link to="/login" className="font-semibold text-accent-light hover:text-accent transition-colors">
            Back to sign in
          </Link>
        </p>
      </div>
    </div>
  );
}

function Centered({ children }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-primary p-4">
      <div className="w-full max-w-[420px] text-center">{children}</div>
    </div>
  );
}

// GoTrue's raw strings ("otp_expired", "Email link is invalid or has expired")
// are not something to show a patient or a paralegal.
function friendlyLinkError(raw) {
  const s = String(raw).toLowerCase();
  if (s.includes('expired') || s.includes('invalid')) {
    return 'This password reset link has expired or was already used. Reset links are valid for one hour and can only be opened once.';
  }
  if (s.includes('access_denied')) {
    return 'This password reset link is no longer valid. Request a new one below.';
  }
  return 'We could not verify this password reset link. Request a new one below.';
}
